import { useCallback, useState } from 'react';
import axios from 'axios';
import { Clock3, Mail, MapPinned, MessageCircle, Phone } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import TurnstileWidget from '../components/TurnstileWidget';
import { PUBLIC_CONTACT_EMAIL, TURNSTILE_SITE_KEY } from '../config/contact';

export default function ContactPage() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleTurnstileError = useCallback(() => {
    setError('La vérification anti-robot est indisponible. Merci de réessayer.');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError('Merci de terminer la vérification anti-robot.');
      return;
    }

    setLoading(true);
    setSuccess('');
    setError('');
    try {
      await axios.post('/api/email/contact', {
        email,
        message,
        website,
        turnstileToken
      });
      setSuccess('Votre message a bien été envoyé. Nous vous répondrons rapidement !');
      setEmail('');
      setMessage('');
      setWebsite('');
    } catch (err: unknown) {
      const apiMessage = axios.isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(apiMessage || `Une erreur est survenue. Merci de réessayer ou de nous contacter directement à ${PUBLIC_CONTACT_EMAIL}`);
    } finally {
      setLoading(false);
      setTurnstileToken('');
      setTurnstileResetKey((current) => current + 1);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <PageHeader eyebrow="Premier contact" title="Venez essayer le Taï-Jitsu." description="Une question sur les cours, l'inscription ou la tenue ? Écrivez-nous : l'équipe du club vous répondra rapidement." icon={<MessageCircle />} />
      <div className="grid gap-6 lg:grid-cols-[.8fr_1.2fr]">
        <aside className="space-y-4">
          <a href="tel:+33610010588" className="card flex items-center gap-4 no-underline"><span className="page-header-icon"><Phone /></span><span><strong className="block text-gray-900">06 10 01 05 88</strong><small className="text-gray-500">Appeler Pierre</small></span></a>
          <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`} className="card flex items-center gap-4 no-underline"><span className="page-header-icon"><Mail /></span><span><strong className="block text-gray-900">{PUBLIC_CONTACT_EMAIL}</strong><small className="text-gray-500">Écrire au club</small></span></a>
          <div className="card flex items-start gap-4"><span className="page-header-icon"><MapPinned /></span><span><strong className="block text-gray-900">Loriol-sur-Drôme</strong><small className="text-gray-500">Dojo Confluence · Gymnase des Écoles</small></span></div>
          <div className="card flex items-start gap-4"><span className="page-header-icon"><Clock3 /></span><span><strong className="block text-gray-900">Cours d'essai gratuit</strong><small className="text-gray-500">Venez en tenue de sport</small></span></div>
        </aside>
        <section className="card p-6 sm:p-8">
          <h2 className="mb-2 text-2xl font-bold">Parlons de votre première séance</h2>
          <p className="mb-7 text-gray-600">Laissez vos coordonnées et quelques mots sur votre demande.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="contact-website">Votre site internet</label>
              <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} />
            </div>
            <div><label className="mb-2 block text-sm font-medium text-gray-700">Votre email</label><input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" placeholder="votre@email.com" /></div>
            <div><label className="mb-2 block text-sm font-medium text-gray-700">Votre message</label><textarea required value={message} onChange={e => setMessage(e.target.value)} className="input-field w-full min-w-0 px-4 py-3" rows={7} placeholder="Je souhaite découvrir le club..." /></div>
            <div className="space-y-2">
              <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} resetKey={turnstileResetKey} onTokenChange={setTurnstileToken} onError={handleTurnstileError} />
              <p className="text-xs text-gray-500">Cette vérification protège le club contre les messages automatisés.</p>
            </div>
            <button type="submit" className="btn-primary w-full" disabled={loading || !turnstileToken || !TURNSTILE_SITE_KEY}>{loading ? 'Envoi en cours...' : 'Envoyer mon message'}</button>
            {success && <div role="status" aria-live="polite" className="rounded-xl bg-green-50 p-3 text-center text-green-700">{success}</div>}
            {error && <div role="alert" aria-live="assertive" className="rounded-xl bg-red-50 p-3 text-center text-red-700">{error}</div>}
          </form>
        </section>
      </div>
    </div>
  );
}
